import { useEffect, useRef } from 'react';
import { notification, Button } from 'antd';
import { t } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';

/**
 * 「功能已更新，点击刷新」提示。
 *
 * 触发时机：**每次登录后只检查一次**（登录态就绪时）。
 * 刻意不做定时轮询 / 切标签页检查 —— 一天发布多次时，那些机制会让同一个用户被
 * 同一条提示反复打扰（发布 10 次就弹 10 次）。想用上新版刷新页面即可。
 *
 * 判断方式：记下本页正在运行的 main.<hash>.js，与 index.html 当前引用的对比。
 *  - index.html 在 nginx 里配了 `no-cache, no-store, must-revalidate`，
 *    重拉一定拿到最新引用，不会被浏览器缓存骗到。
 *  - **刻意不做静默刷新**：审单/录单时可能在填长表单，直接 reload 会打断输入。
 *    这里只弹一条常驻提示，由用户自己点「立即刷新」。
 *  - 文案走 t()，跟随当前语言。
 *  - 同一个登录会话（同一 uid）只提示一次；换账号或刷新页面后会重新检查。
 *  - 开发环境（webpack dev server）没有 main.<hash>.js，自动跳过。
 */
const normalize = (src: string) => src.replace(/^.*?(\/static\/js\/main\.[^/?#]+\.js).*$/, '$1');

const getLoadedMainSrc = (): string | null => {
  const el = document.querySelector('script[src*="/static/js/main."]') as HTMLScriptElement | null;
  return el?.getAttribute('src') || null;
};

const getLatestMainSrc = async (): Promise<string | null> => {
  const res = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/\/static\/js\/main\.[^"'?#]+\.js/);
  return m ? m[0] : null;
};

export const useAppVersionCheck = () => {
  const { user } = useAuth();
  // 记住「已经为哪个用户提示过」，同一登录会话内不重复弹
  const notifiedForRef = useRef<number | undefined>(undefined);
  const uid = user?.id;

  useEffect(() => {
    // 未登录不提示
    if (!uid) return;
    // 这个登录会话已经提示过，不再打扰
    if (notifiedForRef.current === uid) return;

    const loadedRaw = getLoadedMainSrc();
    if (!loadedRaw) return;
    const loaded = normalize(loadedRaw);

    let cancelled = false;
    (async () => {
      try {
        const latestRaw = await getLatestMainSrc();
        if (cancelled || !latestRaw) return;
        if (notifiedForRef.current === uid) return;
        if (normalize(latestRaw) === loaded) return;
        notifiedForRef.current = uid;
        notification.warning({
          key: 'app-version-updated',
          message: t('功能已更新'),
          description: t('当前页面是旧版本，点击刷新即可使用最新功能（未提交的内容会自动保留）'),
          duration: 0,
          placement: 'bottomRight',
          btn: (
            <Button type="primary" size="small" onClick={() => window.location.reload()}>
              {t('立即刷新')}
            </Button>
          ),
        });
      } catch {
        /* 网络异常忽略（下次登录再查） */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid]);
};

export default useAppVersionCheck;
